import { useEffect, useRef, useState } from "react";
import { ChevronDown, ListTree, X } from "lucide-react";
import { tocEntries } from "../../lib/content";

const scrollToHeading = (id) => {
  const el = document.getElementById(id);
  if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 90, behavior: "smooth" });
};

/** Partie en cours de lecture : le dernier titre passé sous le haut de l'écran (en-tête compris). */
function useCurrentHeading(ids) {
  const [current, setCurrent] = useState(null);
  useEffect(() => {
    if (!ids) return undefined;
    const list = ids.split("|");
    let raf = 0;
    const update = () => {
      raf = 0;
      let cur = null;
      for (const id of list) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top < 120) cur = id;
      }
      setCurrent(cur);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [ids]);
  return current;
}

/**
 * Sommaire affiché en haut d'un cours ou d'une analyse (si activé dans l'éditeur). accent : "orange" | "violet"
 * floating : sous 1280 px, un petit bouton rond apparaît en bas à droite quand ce sommaire est sorti de l'écran ;
 * il ouvre le sommaire dans un panneau, sans remonter en haut de la page.
 * raised : remonte ce bouton (sur téléphone) pour ne pas le poser sur le panneau « Vue du lecteur » des admins.
 */
export default function TableOfContents({ html, toc, accent = "orange", className = "", floating = false, raised = false }) {
  const [open, setOpen] = useState(() => typeof window === "undefined" || window.innerWidth >= 768);
  const navRef = useRef(null);
  if (!toc?.enabled) return null;
  const entries = tocEntries(html, toc).filter((e) => !e.hidden);
  if (entries.length < 2) return null;
  const color = accent === "violet" ? "text-violet-300" : "text-orange-300";
  const hoverNum = accent === "violet" ? "group-hover:text-violet-300" : "group-hover:text-orange-300";
  const go = (e, id) => {
    e.preventDefault();
    scrollToHeading(id);
  };
  return (
    <>
    {floating && <TocFloating navRef={navRef} entries={entries} accent={accent} raised={raised} />}
    <nav ref={navRef} className={`not-prose my-8 rounded-xl border border-white/10 bg-white/[0.02] ${className}`} aria-label="Sommaire" data-testid="toc">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-3.5 text-left">
        <span className={`text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2 ${color}`}><ListTree className="w-3.5 h-3.5" /> Sommaire</span>
        <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ol className="px-5 pb-4 space-y-1.5 text-[15px]">
          {entries.map((e, i) => (
            <li key={e.id} className={e.level === 3 ? "pl-5 text-sm" : ""}>
              <a href={`#${e.id}`} onClick={(ev) => go(ev, e.id)} className="group flex gap-3 text-zinc-300 hover:text-white">
                {e.level === 2 && <span className={`tabular-nums text-zinc-600 ${hoverNum}`}>{String(entries.filter((x, k) => k <= i && x.level === 2).length).padStart(2, "0")}</span>}
                <span>{e.label}</span>
              </a>
            </li>
          ))}
        </ol>
      )}
    </nav>
    </>
  );
}

/**
 * Bouton rond « Sommaire » pendant la lecture (sous 1280 px ; au-delà, le sommaire est déjà fixé sur le côté).
 * Visible seulement quand le sommaire du haut est sorti de l'écran et qu'il reste du texte à lire.
 * Il ouvre un panneau qui monte du bas, avec la partie en cours mise en avant.
 */
function TocFloating({ navRef, entries, accent, raised }) {
  const [visible, setVisible] = useState(false);
  const [sheet, setSheet] = useState(false);
  const current = useCurrentHeading(entries.map((e) => e.id).join("|"));
  const violet = accent === "violet";

  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const nav = navRef.current;
      const body = nav?.parentElement; // le bloc du texte : le bouton disparaît une fois la lecture finie
      if (!nav || !body) return;
      const passed = nav.getBoundingClientRect().bottom < 64;
      const reading = body.getBoundingClientRect().bottom > window.innerHeight * 0.6;
      setVisible(passed && reading);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [navRef]);

  // Panneau ouvert : la page derrière ne défile plus ; Échap le ferme
  useEffect(() => {
    if (!sheet) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && setSheet(false);
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [sheet]);

  const pick = (e, id) => {
    e.preventDefault();
    setSheet(false);
    requestAnimationFrame(() => scrollToHeading(id)); // une fois la page de nouveau libre de défiler
  };
  const pos = raised ? "bottom-[8.5rem] md:bottom-6 md:left-4 md:right-auto" : "bottom-20 md:bottom-6";

  return (
    <>
      <button
        type="button"
        onClick={() => setSheet(true)}
        aria-label="Afficher le sommaire"
        tabIndex={visible ? 0 : -1}
        className={`xl:hidden fixed right-4 ${pos} z-40 grid place-items-center w-11 h-11 rounded-full border bg-[#121214]/95 shadow-lg shadow-black/40 transition-all duration-200 ${
          violet ? "border-violet-500/35 text-violet-300" : "border-orange-500/35 text-orange-300"
        } ${visible && !sheet ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3 pointer-events-none"}`}
        style={{ marginBottom: "env(safe-area-inset-bottom)" }}
        data-testid="toc-floating-btn"
      >
        <ListTree className="w-[18px] h-[18px]" />
      </button>

      {sheet && (
        <div className="xl:hidden fixed inset-0 z-[70]" data-testid="toc-sheet">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSheet(false)} />
          <nav
            className="absolute inset-x-0 bottom-0 mx-auto max-w-lg max-h-[70vh] overflow-y-auto overscroll-contain rounded-t-2xl border-t border-white/10 bg-[#121214] px-5 pt-2 animate-in slide-in-from-bottom duration-200"
            style={{ paddingBottom: "calc(1.25rem + env(safe-area-inset-bottom))" }}
            aria-label="Sommaire"
          >
            <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-white/15" />
            <div className="flex items-center justify-between pb-3 mb-1 border-b border-white/5">
              <span className={`text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2 ${violet ? "text-violet-300" : "text-orange-300"}`}>
                <ListTree className="w-3.5 h-3.5" /> Sommaire
              </span>
              <button type="button" onClick={() => setSheet(false)} className="p-2 -mr-2 text-zinc-500" aria-label="Fermer">
                <X className="w-5 h-5" />
              </button>
            </div>
            <ol className="text-[15px]">
              {entries.map((e, i) => {
                const on = current === e.id;
                return (
                  <li key={e.id}>
                    <a
                      href={`#${e.id}`}
                      onClick={(ev) => pick(ev, e.id)}
                      className={`flex gap-3 py-2.5 border-l-2 -ml-px leading-snug ${e.level === 3 ? "pl-8 text-sm" : "pl-3"} ${
                        on ? `${violet ? "border-violet-400" : "border-orange-400"} text-white` : "border-transparent text-zinc-400"
                      }`}
                    >
                      {e.level === 2 && (
                        <span className={`tabular-nums ${on ? (violet ? "text-violet-300" : "text-orange-300") : "text-zinc-600"}`}>
                          {String(entries.filter((x, k) => k <= i && x.level === 2).length).padStart(2, "0")}
                        </span>
                      )}
                      <span>{e.label}</span>
                    </a>
                  </li>
                );
              })}
            </ol>
          </nav>
        </div>
      )}
    </>
  );
}

/**
 * Sommaire fixé sur le côté, pour les grands écrans (≥ 1280 px) : il reste visible pendant la lecture
 * et met en avant la partie en cours. À placer dans un parent `position: relative` qui couvre tout le texte
 * (il se cale à gauche de la colonne de texte). accent : "orange" | "violet"
 */
export function TocAside({ html, toc, accent = "orange", className = "" }) {
  const entries = toc?.enabled ? tocEntries(html, toc).filter((e) => !e.hidden) : [];
  const ids = entries.map((e) => e.id).join("|");
  const current = useCurrentHeading(ids);
  // Replié / déplié d'un clic sur « Sommaire » ; le choix est gardé pour les analyses suivantes (sur cet appareil)
  const [open, setOpen] = useState(() => {
    try { return window.localStorage.getItem("sb_toc_aside") !== "closed"; } catch { return true; }
  });
  const toggle = () => {
    setOpen((o) => {
      try { window.localStorage.setItem("sb_toc_aside", o ? "closed" : "open"); } catch { /* stockage indisponible */ }
      return !o;
    });
  };
  if (entries.length < 2) return null;
  const violet = accent === "violet";
  const label = violet ? "text-violet-300" : "text-orange-300";
  const bar = violet ? "border-violet-400" : "border-orange-400";
  const go = (e, id) => {
    e.preventDefault();
    scrollToHeading(id);
  };
  return (
    <aside className={`absolute top-0 bottom-0 right-full mr-10 w-56 ${className}`} data-testid="toc-aside">
      <nav className="sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto" aria-label="Sommaire">
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          className={`mb-3 text-[11px] uppercase tracking-[0.2em] inline-flex items-center gap-2 hover:text-white ${label}`}
          data-testid="toc-aside-toggle"
        >
          <ListTree className="w-3.5 h-3.5" /> Sommaire
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
        <ol className="border-l border-white/10 text-sm">
          {entries.map((e) => (
            <li key={e.id}>
              <a
                href={`#${e.id}`}
                onClick={(ev) => go(ev, e.id)}
                className={`-ml-px block border-l-2 py-1.5 leading-snug transition-colors ${e.level === 3 ? "pl-7 text-[13px]" : "pl-4"} ${
                  current === e.id ? `${bar} text-white` : "border-transparent text-zinc-500 hover:text-zinc-200"
                }`}
              >
                {e.label}
              </a>
            </li>
          ))}
        </ol>
        )}
      </nav>
    </aside>
  );
}
